import { Trash2 } from "lucide-react";
import AppButton from "../../../../components/ui/AppButton";
import AppDatePicker from "../../../../components/ui/AppDatePicker";
import AppInputField from "../../../../components/ui/AppInputField";
import { MySelect } from "../../../../components/ui/Select";
import ExpenseHeadComboInput from "../../../finance/components/ExpenseHeadDropDownMenuInput";
import { FuelExpense } from "../../@types";

interface OtherExpense {
  description: string;
  amount: number;
}

interface ExpensesStepProps {
  fuelExpenses: FuelExpense;
  setFuelExpenses: React.Dispatch<React.SetStateAction<FuelExpense>>;
  otherExpenses: OtherExpense[];
  setOtherExpenses: React.Dispatch<React.SetStateAction<OtherExpense[]>>;
  onNext: () => void;
  onBack: () => void;
}

const ExpensesStep = ({
  fuelExpenses,
  setFuelExpenses,
  otherExpenses,
  setOtherExpenses,
  onNext,
  onBack,
}: ExpensesStepProps) => {
  const handleAddOtherExpense = () => {
    setOtherExpenses([...otherExpenses, { description: "", amount: 0 }]);
  };

  const handleRemoveOtherExpense = (index: number) => {
    setOtherExpenses(otherExpenses.filter((_, i) => i !== index));
  };

  const handleUpdateOtherExpense = (
    index: number,
    field: keyof OtherExpense,
    value: string | number
  ) => {
    const updated = [...otherExpenses];
    updated[index] = { ...updated[index], [field]: value };
    setOtherExpenses(updated);
  };

  const handleUpdateFuelExpenses = <K extends keyof FuelExpense>(
    field: K,
    value: FuelExpense[K]
  ) => {
    setFuelExpenses((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-4">
        <h3 className="text-[13px] font-semibold">Fixed Expense</h3>
        <div className="border border-gray-100 rounded-[10px] p-2 items-center justify-center space-y-2">
          <div className="text-[13px] font-normal">Fuel Purchase</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <AppInputField
              type="number"
              label="Quantities"
              value={fuelExpenses.quantity}
              onChange={(e) =>
                handleUpdateFuelExpenses("quantity", e.target.valueAsNumber)
              }
              className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
              placeholder="Enter liters"
            />
            <AppInputField
              type="number"
              label="Price per unit"
              value={fuelExpenses.pricePerUnit}
              onChange={(e) =>
                handleUpdateFuelExpenses("pricePerUnit", e.target.valueAsNumber)
              }
              className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
              placeholder="Enter price per unit"
            />
            <AppInputField
              type="number"
              label="Amount"
              value={fuelExpenses.amount}
              onChange={(e) =>
                handleUpdateFuelExpenses("amount", e.target.valueAsNumber)
              }
              className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
              placeholder="Enter amount"
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* <AppInputField
              type="text"
              label="Fuel Type"
              value={fuelExpenses.fuelType}
              onChange={(e) =>
                handleUpdateFuelExpenses("fuelType", e.target.value)
              }
              className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
              placeholder="Enter fuel type"
            /> */}

            <MySelect<string>
              name={"name"}
              id={"id"}
              label="Fuel Type"
              value={fuelExpenses.fuelType}
              options={["Petrol", "Diesel"]}
              getValue={(option: string) => option}
              getName={(option: string) => option}
              onChange={(option: string) =>
                handleUpdateFuelExpenses("fuelType", option)
              }
              onValChange={(option: string) =>
                handleUpdateFuelExpenses("fuelType", option)
              }
              placeholder={"Select fuel type"}
              className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
            />
            <AppDatePicker
              label="Fuel Date"
              value={
                fuelExpenses.fuelDate ? new Date(fuelExpenses.fuelDate) : null
              }
              onChange={(date) =>
                handleUpdateFuelExpenses(
                  "fuelDate",
                  date ? date.toISOString().split("T")[0] : ""
                )
              }
              className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
            />

            <AppInputField
              type="number"
              label="Odometer reading"
              value={fuelExpenses.odometerReading}
              onChange={(e) =>
                handleUpdateFuelExpenses(
                  "odometerReading",
                  e.target.valueAsNumber
                )
              }
              className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
              placeholder="Enter odometer reading"
            />
            <AppInputField
              type="text"
              label="Station name"
              value={fuelExpenses.stationName}
              onChange={(e) =>
                handleUpdateFuelExpenses("stationName", e.target.value)
              }
              className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
              placeholder="Enter station name"
            />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-[13px] font-semibold">Other Expenses</h3>
        <div className="border border-gray-100 rounded-[10px] p-4 items-center justify-center">
          {otherExpenses.map((expense, index) => (
            <div
              key={index}
              className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 w-full"
            >
              {/* <AppInputField
                label={`Description ${index + 1}`}
                type="text"
                value={expense.description}
                onChange={(e) =>
                  handleUpdateOtherExpense(index, "description", e.target.value)
                }
                className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] w-full"
                placeholder="Enter description"
              /> */}

              <ExpenseHeadComboInput
                label={`Description ${index + 1}`}
                type="text"
                value={expense.description}
                onChange={(e) =>
                  handleUpdateOtherExpense(index, "description", e.target.value)
                }
                className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] w-full"
                placeholder="Enter description"
              />

              <AppInputField
                label={`Amount ${index + 1}`}
                type="number"
                value={expense.amount || ""}
                onChange={(e) =>
                  handleUpdateOtherExpense(
                    index,
                    "amount",
                    parseFloat(e.target.value) || 0
                  )
                }
                className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] w-full"
                placeholder="Enter amount"
              />
              <div className="w-full md:w-[48px] bg-red-100 flex items-center justify-center h-[48px] rounded-[10px] mt-0 md:mt-8">
                <button
                  type="button"
                  onClick={() => handleRemoveOtherExpense(index)}
                  className="text-red-500 hover:text-red-600 transition-colors"
                  aria-label="Remove crew member"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddOtherExpense}
            className="text-secondary font-semibold hover:text-primary transition-colors text-[13px]"
          >
            + Add Expense
          </button>
        </div>
      </div>

      <div className="flex justify-start items-start space-x-4 pt-4">
        <AppButton
          type="button"
          onClick={onBack}
          className="px-6 py-2 bg-[#F6F6F6] rounded-md text-gray-700 hover:bg-gray-50 transition-colors text-[15px]"
        >
          Back
        </AppButton>

        <AppButton
          type="button"
          onClick={onNext}
          className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors text-[15px]"
        >
          Continue
        </AppButton>
      </div>
    </div>
  );
};

export default ExpensesStep;
